import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import * as constants from 'src/app/common/constant/constants.type';
import { MY_FORMATS } from 'src/app/common/dateFormat';
import { toNativeDate } from 'src/app/common/function-helper';
import { MaterialModule } from 'src/app/material.module';
import { TimeConvertPipe } from 'src/app/pipe/time-convert.pipe';
import { AppointmentService } from '../appointment.service';
import { AppointmentSlot } from '../iappointment';

export interface FollowUpSlotValue {
  appointDate: Date | null;
  appointTime: string | null;
}

@Component({
  selector: 'app-follow-up-slot-picker',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    TablerIconsModule,
  ],
  providers: [
    DatePipe,
    TimeConvertPipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  templateUrl: './follow-up-slot-picker.component.html',
  styleUrl: './follow-up-slot-picker.component.scss',
})
export class FollowUpSlotPickerComponent implements OnChanges, OnDestroy {
  @Input() doctorId: number | null | undefined;
  @Input() disabled = false;
  @Input() required = true;
  @Input() initialValue: FollowUpSlotValue | null = null;
  @Output() valueChange = new EventEmitter<FollowUpSlotValue>();

  followUpForm: FormGroup;
  timeSlots: AppointmentSlot[] = [];
  minDate = new Date();
  isLoadingSlots = false;

  private unavailableDays: string[] = [...constants.DAYS];
  private readonly destroy$ = new Subject<void>();
  private readonly slotsReset$ = new Subject<void>();
  private readonly dayMap: Record<string, number> = {
    sun: 0,
    mon: 1,
    tue: 2,
    wed: 3,
    thu: 4,
    fri: 5,
    sat: 6,
  };
  private availabilityLoadId = 0;

  constructor(
    private readonly fb: FormBuilder,
    private readonly appointmentService: AppointmentService,
    private readonly datePipe: DatePipe,
    private readonly timeConvertPipe: TimeConvertPipe,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService,
  ) {
    this.followUpForm = this.fb.group({
      appointDate: [null, Validators.required],
      appointTime: [null, Validators.required],
    });

    this.followUpForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.valueChange.emit(this.value));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['required']) {
      this.updateValidators();
    }

    if (changes['disabled']) {
      this.updateDisabledState();
    }

    if (changes['doctorId']) {
      this.resetSlots();
      this.unavailableDays = [...constants.DAYS];
      if (!changes['doctorId'].firstChange || !this.initialValue) {
        this.followUpForm.reset({ appointDate: null, appointTime: null }, { emitEvent: true });
      }
      this.loadDoctorAvailability();
    }

    if (changes['initialValue']) {
      this.applyInitialValue();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.slotsReset$.complete();
  }

  get value(): FollowUpSlotValue {
    return {
      appointDate: this.followUpForm.get('appointDate')?.value ?? null,
      appointTime: this.followUpForm.get('appointTime')?.value ?? null,
    };
  }

  get hasSelection(): boolean {
    return !!this.value.appointDate && !!this.value.appointTime;
  }

  get hasPartialSelection(): boolean {
    return (!!this.value.appointDate || !!this.value.appointTime) && !this.hasSelection;
  }

  get invalid(): boolean {
    return this.followUpForm.invalid;
  }

  markAllAsTouched(): void {
    this.followUpForm.markAllAsTouched();
  }

  get canClear(): boolean {
    return !this.required && !this.disabled && !!(this.value.appointDate || this.value.appointTime);
  }

  clearFollowUp(): void {
    if (!this.canClear) {
      return;
    }
    this.resetSlots();
    this.followUpForm.reset({ appointDate: null, appointTime: null });
  }

  private resetSlots(): void {
    this.slotsReset$.next();
    this.timeSlots = [];
    this.isLoadingSlots = false;
  }

  onDateChange(event: MatDatepickerInputEvent<Date>): void {
    this.resetSlots();
    this.followUpForm.get('appointTime')?.setValue(null);

    if (!event.value || !this.doctorId) {
      return;
    }

    this.loadAvailableSlots(event.value);
  }

  dateFilter = (date: Date | null): boolean => {
    if (!date) {
      return false;
    }

    const selected = this.toDateOnly(date);
    if (!selected) {
      return false;
    }

    const today = this.toDateOnly(new Date());
    if (!today || selected <= today) {
      return false;
    }

    return this.isDayAvailable(selected.getDay());
  };

  private loadDoctorAvailability(): void {
    const doctorId = this.doctorId ? Number(this.doctorId) : null;
    const loadId = ++this.availabilityLoadId;
    this.unavailableDays = [...constants.DAYS];

    if (!doctorId) {
      this.updateDateValidity();
      return;
    }

    const formattedDate = this.datePipe.transform(this.minDate, constants.DATE_FORMAT);
    if (!formattedDate) {
      this.updateDateValidity();
      return;
    }

    this.appointmentService
      .getAppointmentSlot({ date: formattedDate, doctorId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (loadId !== this.availabilityLoadId) {
            return;
          }

          this.applyAvailableDays(response?.result?.daysAvailable);
          this.updateDateValidity();
        },
        error: () => {
          if (loadId !== this.availabilityLoadId) {
            return;
          }

          this.unavailableDays = [...constants.DAYS];
          this.updateDateValidity();
        },
      });
  }

  private applyAvailableDays(availableDays: string[] | null | undefined): void {
    if (availableDays?.length) {
      const available = availableDays.map((day) => day.toLowerCase());
      this.unavailableDays = constants.DAYS.filter((day) => !available.includes(day.toLowerCase()));
      return;
    }

    this.unavailableDays = [...constants.DAYS];
  }

  private isDayAvailable(dayOfWeek: number): boolean {
    return !this.unavailableDays.some((day) => this.dayMap[day.toLowerCase()] === dayOfWeek);
  }

  private updateDateValidity(): void {
    this.followUpForm.get('appointDate')?.updateValueAndValidity({ emitEvent: false });
  }

  private updateValidators(): void {
    const controls = ['appointDate', 'appointTime'];
    controls.forEach((controlName) => {
      const control = this.followUpForm.get(controlName);
      if (!control) {
        return;
      }
      control.setValidators(this.required ? Validators.required : null);
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  private updateDisabledState(): void {
    if (this.disabled) {
      this.followUpForm.disable({ emitEvent: false });
      return;
    }

    this.followUpForm.enable({ emitEvent: false });
  }

  private applyInitialValue(): void {
    this.resetSlots();
    if (!this.initialValue?.appointDate) {
      this.followUpForm.reset({ appointDate: null, appointTime: null }, { emitEvent: true });
      return;
    }

    const selectedDate = toNativeDate(this.initialValue.appointDate);
    if (!selectedDate) {
      return;
    }

    const selectedTime = this.toDisplayTime(this.initialValue.appointTime);
    this.timeSlots = selectedTime ? [{ time: selectedTime, appointment: null }] : [];
    this.followUpForm.patchValue(
      {
        appointDate: selectedDate,
        appointTime: selectedTime,
      },
      { emitEvent: true },
    );

    if (!this.disabled && this.doctorId) {
      this.loadAvailableSlots(selectedDate, selectedTime);
    }
  }

  private loadAvailableSlots(date: Date, selectedTime?: string | null): void {
    if (!this.doctorId) {
      return;
    }

    const selectedDate = toNativeDate(date);
    if (!selectedDate) {
      return;
    }

    const formattedDate = this.datePipe.transform(
      selectedDate,
      constants.DATE_FORMAT,
    );
    if (!formattedDate) {
      return;
    }

    this.isLoadingSlots = true;

    this.appointmentService
      .getAppointmentSlot({ date: formattedDate, doctorId: this.doctorId })
      .pipe(takeUntil(this.slotsReset$), takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (!response?.result) {
            this.timeSlots = [];
            this.isLoadingSlots = false;
            this.toastr.error(
              this.translate.instant('APPOINTMENT_RELATED.DOCTOR_AVAILABILITY'),
            );
            return;
          }

          const slots = Array.isArray(response.result.timeSlots)
            ? response.result.timeSlots
            : [];
          this.timeSlots = slots.filter(
            (slot) =>
              (!slot.appointment || slot.time === selectedTime) && !this.isPastSlot(selectedDate, slot.time),
          );

          if (selectedTime && !this.timeSlots.some((slot) => slot.time === selectedTime)) {
            this.timeSlots = [{ time: selectedTime, appointment: null }, ...this.timeSlots];
          }
          this.isLoadingSlots = false;

          if (this.timeSlots.length === 0) {
            this.toastr.error(
              this.translate.instant('APPOINTMENT_RELATED.ALREADY_BOOKED_SLOT'),
            );
          }
        },
        error: () => {
          this.timeSlots = [];
          this.isLoadingSlots = false;
          this.toastr.error(
            this.translate.instant('APPOINTMENT_RELATED.DOCTOR_AVAILABILITY'),
          );
        },
      });
  }

  private isPastSlot(date: Date, displayTime: string): boolean {
    const selectedDate = this.toDateOnly(date);
    const today = this.toDateOnly(new Date());
    if (!selectedDate || !today || selectedDate > today) {
      return false;
    }

    const convertedTime = this.timeConvertPipe.transform(displayTime);
    if (!convertedTime || !convertedTime.includes(':')) {
      return false;
    }
    const [hours, minutes] = convertedTime.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return false;
    }

    const slotDateTime = new Date(date);
    slotDateTime.setHours(hours, minutes, 0, 0);
    return slotDateTime < new Date();
  }

  private toDateOnly(value: Date | null): Date | null {
    const date = toNativeDate(value);
    if (!date) {
      return null;
    }

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    date.setHours(0, 0, 0, 0);
    return date;
  }

  private toDisplayTime(value: string | null): string | null {
    if (!value) {
      return null;
    }

    const amPmMatch = value.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (amPmMatch) {
      return `${Number(amPmMatch[1])}:${amPmMatch[2]} ${amPmMatch[3].toUpperCase()}`;
    }

    const timeMatch = value.match(/^(\d{1,2}):(\d{2})/);
    if (!timeMatch) {
      return value;
    }

    const hours = Number(timeMatch[1]);
    const minutes = timeMatch[2];
    if (Number.isNaN(hours)) {
      return value;
    }

    const displayHour = hours % 12 || 12;
    const suffix = hours >= 12 ? 'PM' : 'AM';
    return `${displayHour}:${minutes} ${suffix}`;
  }

}
